import { SupportTrip } from "domain/entities/support-trip";
import { Parameter } from "domain/entities/parameter";
import { DistanceTypeEnum } from "domain/enum/distanceType";
import { EMAIL } from "domain/enum/email";
import { MessageTypeEnum } from "domain/enum/messageType";
import { DistanceInputFilter } from "domain/models/distance-input-filter";
import MobileDropdownComponent from "presentation/mobile/components/MobileDropdownComponent";
import MobileInputDateComponent from "presentation/mobile/components/MobileInputDateComponent";
import { useTripLocationsByFilter } from "presentation/hooks/use-query-distance";
import { useCreateSupportTripMutation } from "presentation/hooks/use-query-support-trip";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { Button } from "primereact/button";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { useEffect, useMemo, useState } from "react";

interface RegisterTripFormProps {
    onClose: () => void;
    showMessage: (type: MessageTypeEnum, subject: string, message: string) => void;
    isFree: boolean;
}

const RegisterTripForm = ({ onClose, showMessage, isFree }: RegisterTripFormProps) => {
    const { userEmployee, email: userEmployeeEmail } = useAuthStore();

    const [trip, setTrip] = useState<Partial<SupportTrip>>({
        locationOrigin: "",
        locationDestination: "",
        dateTrip: undefined,
        distanceKm: undefined,
    });

    // ===== ORIGENS (só no modo suporte) =====
    const { data: originsData } = useTripLocationsByFilter({
        type: DistanceTypeEnum.VIAGEM_SUPORTE,
        sortBy: "locationOrigin",
        sortOrder: "asc",
        page: 1,
        limit: 500,
    } as DistanceInputFilter);

    const origins: Parameter[] = useMemo(() => {
        const map = new Map<string, Parameter>();
        originsData?.data?.forEach((item) => {
            const key = item.locationOrigin?.trim();
            if (key && !map.has(key)) {
                map.set(key, {
                    id: item.locationOrigin,
                    value: item.locationOrigin,
                    type: "tripOrigin",
                });
            }
        });
        return Array.from(map.values());
    }, [originsData]);

    // ===== DESTINOS (dependem da origem) =====
    const { data: destinationsData } = useTripLocationsByFilter({
        type: DistanceTypeEnum.VIAGEM_SUPORTE,
        locationOrigin: trip.locationOrigin,
        sortBy: "locationDestination",
        sortOrder: "asc",
        page: 1,
        limit: 500,
    } as DistanceInputFilter);

    const destinations: Parameter[] = useMemo(() => {
        const map = new Map<string, Parameter>();
        destinationsData?.data?.forEach((item) => {
            const key = item.locationDestination?.trim();
            if (key && !map.has(key)) {
                map.set(key, {
                    id: item.locationDestination,
                    value: item.locationDestination,
                    type: "tripDestination",
                });
            }
        });
        return Array.from(map.values());
    }, [destinationsData]);

    // Reset destino quando muda origem (só no modo suporte)
    useEffect(() => {
        if (!isFree) {
            setTrip((prev) => ({ ...prev, locationDestination: "" }));
        }
      //  console.log("[RegisterTripForm] isFree =", isFree);
    }, [trip.locationOrigin, isFree]);

    // ===== MUTATION =====
    const {
        mutate: createTrip,
        isPending,
        isSuccess,
        isError,
        error,
    } = useCreateSupportTripMutation();

    const handleSave = () => {
        if (!trip.dateTrip) {
            showMessage(MessageTypeEnum.WARN, "Aviso", "Escolhe a data da viagem.");
            return;
        }
        if (!trip.locationOrigin?.trim()) {
            showMessage(MessageTypeEnum.WARN, "Aviso", "Preenche a origem.");
            return;
        }
        if (!trip.locationDestination?.trim()) {
            showMessage(MessageTypeEnum.WARN, "Aviso", "Preenche o destino.");
            return;
        }

        // Só valida distanceKm no modo livre
        if (isFree && (!trip.distanceKm || trip.distanceKm <= 0)) {
            showMessage(MessageTypeEnum.WARN, "Aviso", "A distância tem de ser superior a 0.");
            return;
        }

        const payload: Partial<SupportTrip> = {
            dateTrip: trip.dateTrip,
            locationOrigin: trip.locationOrigin,
            locationDestination: trip.locationDestination,
            employee: {
                id: userEmployee?.id ?? "",
                name: userEmployee?.name ?? "",
            },
            lastModifiedBy: userEmployeeEmail || EMAIL,
            ...(isFree ? { distanceKm: trip.distanceKm } : {}),
        };

        createTrip(payload);
    };

    useEffect(() => {
        if (isSuccess) {
            showMessage(MessageTypeEnum.SUCCESS, "Sucesso", "Viagem registada com sucesso.");
            onClose();
        }
    }, [isSuccess]);

    useEffect(() => {
        if (isError && error) {
            const err: any = error;
            const messages: string[] = err?.errors?.length
                ? err.errors
                : err?.response?.data?.metadata?.errors ?? [err.message ?? "Erro inesperado"];
            messages.forEach((m: string) => showMessage(MessageTypeEnum.ERROR, "Erro", m));
        }
    }, [isError, error]);

    return (
        <div className="flex flex-column gap-3 p-3 h-full">
            {/* Data — sempre */}
            <div className="flex flex-column gap-1">
                <label className="text-sm font-medium">Data</label>
                <MobileInputDateComponent
                    fieldName="dateTrip"
                    currentObject={trip}
                    getValue={trip.dateTrip}
                    setValue={(updated) => setTrip(updated)}
                    placeholder="dd/mm/yyyy"
                    dateFormat="dd/mm/yy"
                    showButtonBar
                />
            </div>

            {/* ===== MODO LIVRE ===== */}
            {isFree && (
                <>
                    <div className="flex flex-column gap-1">
                        <label className="text-sm font-medium">Origem</label>
                        <InputText
                            value={trip.locationOrigin ?? ""}
                            onChange={(e) => setTrip((prev) => ({ ...prev, locationOrigin: e.target.value }))}
                            placeholder="Local de partida"
                            className="w-full"
                        />
                    </div>

                    <div className="flex flex-column gap-1">
                        <label className="text-sm font-medium">Destino</label>
                        <InputText
                            value={trip.locationDestination ?? ""}
                            onChange={(e) => setTrip((prev) => ({ ...prev, locationDestination: e.target.value }))}
                            placeholder="Local de chegada"
                            className="w-full"
                        />
                    </div>

                    <div className="flex flex-column gap-1">
                        <label className="text-sm font-medium">Distância (km)</label>
                        <InputNumber
                            inputId="distanceKm"
                            value={trip.distanceKm ?? null}
                            onValueChange={(e: InputNumberValueChangeEvent) => {
                                console.log("[InputNumber] e.value =", e.value, "| typeof =", typeof e.value);
                                setTrip((prev) => {
                                    const newTrip = { ...prev, distanceKm: e.value ?? undefined };
                                   // console.log("[setTrip] distanceKm =", newTrip.distanceKm, "| typeof =", typeof newTrip.distanceKm);
                                    return newTrip;
                                });
                            }}
                            mode="decimal"
                            min={0}
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            placeholder="0.0"
                            className="w-full"
                            locale="pt-PT"
                            useGrouping={false}
                        />
                    </div>
                </>
            )}

            {/* ===== MODO SUPORTE ===== */}
            {!isFree && (
                <>
                    <div className="flex flex-column gap-1">
                        <label className="text-sm font-medium">Origem</label>
                        <MobileDropdownComponent
                            fieldName="locationOrigin"
                            options={origins}
                            label="Selecione a origem"
                            currentObject={trip}
                            getValue={trip.locationOrigin}
                            setValue={(updated) => setTrip(updated)}
                        />
                    </div>

                    <div className="flex flex-column gap-1">
                        <label className="text-sm font-medium">Destino</label>
                        <MobileDropdownComponent
                            fieldName="locationDestination"
                            options={destinations}
                            label="Selecione o destino"
                            currentObject={trip}
                            getValue={trip.locationDestination}
                            setValue={(updated) => setTrip(updated)}
                            isDisable={!trip.locationOrigin}
                        />
                    </div>
                </>
            )}

            <div className="mt-auto pt-3">
                <Button
                    label="Adicionar Viagem"
                    icon="pi pi-plus-circle"
                    className="w-full"
                    loading={isPending}
                    onClick={handleSave}
                />
            </div>
        </div>
    );
};

export default RegisterTripForm;